import CircleExclammationIcon from '@src/svgs/CircleExclammationIcon';
import React from 'react';

import {
  ModalButtonsContainer,
  ModalCancelButton,
  ModalConfirmButton,
  ModalHeading,
  ModalHeadingIcon,
  ModalTitle,
  ModalWrapper,
} from '../common/ConfirmModal';
import { DialogHeader, DialogText } from '../common/dialogStyles';
import Modal from '../common/Modal';

type Variant = 'system-issues' | 'user-upload-failed';

type Props = {
  enabled: boolean;
  variant: Variant;
  customMessage?: string | null;
  onClose?: () => void;
  onRetry?: () => void;
  retryable?: boolean;
};

const StatusMessageModal: React.FC<Props> = (props: Props) => {
  const { enabled, variant, customMessage, retryable, onRetry } = props;

  const [isOpen, setIsOpen] = React.useState(true);

  if (!enabled || !isOpen) {
    return null;
  }

  const { title, message } = getCopyByVariant(variant);
  const handleClose = () => {
    setIsOpen(false);
    if (props.onClose) {
      props.onClose();
    }
  };

  const showRetry = retryable && onRetry;

  return (
    <Modal isOpen closeModal={handleClose}>
      <ModalWrapper>
        <ModalHeading>
          <ModalHeadingIcon>
            <CircleExclammationIcon
              width="16"
              height="16"
              strokeColor="var(--destructive)"
            />
          </ModalHeadingIcon>
          <DialogHeader>
            <ModalTitle>{title}</ModalTitle>
            <DialogText>{customMessage ?? message}</DialogText>
          </DialogHeader>
        </ModalHeading>
        <ModalButtonsContainer>
          <ModalCancelButton onClick={handleClose}>Close</ModalCancelButton>
          {showRetry && (
            <ModalConfirmButton accentColor="var(--primary)" onClick={onRetry}>
              Try Again
            </ModalConfirmButton>
          )}
        </ModalButtonsContainer>
      </ModalWrapper>
    </Modal>
  );
};

// A concise title plus a supporting message underneath, per the confirmation
// dialog standard — never one continuous block of heading text.
const getCopyByVariant = (variant: Variant) => {
  switch (variant) {
    case 'system-issues':
      return {
        title: 'We’re Experiencing System Issues',
        message: 'We’re actively investigating. Sorry for the interruption.',
      };
    case 'user-upload-failed':
      return {
        title: 'Upload Failed',
        message: 'Something went wrong. Please try again or contact support.',
      };
  }
};

export default StatusMessageModal;
